import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Slider from 'react-slick'
import ReactPlayer from 'react-player'
import toast from 'react-hot-toast'

import { discoverImages, images } from '../../services/images.service'
import { factService } from '../../services/ClassicAnimes/fact.service.js'
import { getAnimeDetailsUrl, utilService } from '../../services/util.service.js'

import {
  loadStatusMap,
  addItem as addItemThunk,
  removeItem as removeItemThunk,
} from '../../store/slices/watchlist.slice.js'
import { fetchClassics } from '../../store/slices/animeFeatures.slice.js'
import { GreyBorderBottom } from '../ui/elements'
import { TrailerIcon, WatchlistIcon } from '../ui/icons/shared'

export function DiscoverSection() {
  const dispatch = useDispatch()

  const [facts, setFacts] = useState([])
  const [selectedTrailer, setSelected] = useState(null)
  const [hoveredTrailerId, setHover] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  const loggedInUser = useSelector((s) => s.userModule.loggedInUser)
  const trailers = useSelector((s) => s.animeFeatures.classics)
  const loadingClassic = useSelector((s) => s.animeFeatures.loading.classics)
  const errorClassic = useSelector((s) => s.animeFeatures.error)

  const statusMap = useSelector(
    (s) => s.watchlist.statusMap?.[loggedInUser?._id] || {}
  )

  useEffect(() => {
    ;(async () => {
      try {
        setFacts(await factService.fetchFacts())
      } catch (err) {
        console.error('Error loading facts:', err)
      }
    })()
  }, [])

  useEffect(() => {
    dispatch(fetchClassics())
  }, [dispatch])

  useEffect(() => {
    if (!trailers.length) return
    setSelected(trailers[0])
    if (loggedInUser)
      dispatch(
        loadStatusMap({
          userId: loggedInUser._id,
          listType: 'watchlist',
          anizoneIds: trailers.map((t) => t.mal_id),
        })
      )
  }, [trailers, loggedInUser, dispatch])

  const isInWatch = (id) => Boolean(statusMap[id])

  const handleToggleWatch = (anime) => {
    if (!loggedInUser) return toast.error('התחבר כדי לערוך רשימת צפייה')

    if (isInWatch(anime.mal_id)) {
      dispatch(
        removeItemThunk({
          userId: loggedInUser._id,
          listType: 'watchlist',
          anizoneId: anime.mal_id,
        })
      )
      toast.success('הוסר מרשימת הצפייה')
    } else {
      utilService.handleAddItem(
        'Watching',
        anime,
        dispatch,
        loggedInUser,
        [],
        addItemThunk
      )
    }
  }

  const sliderSettings = {
    dots: true,
    rtl: true,
    infinite: true,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    customPaging: (index) => (
      <img
        src={
          index === currentSlide
            ? discoverImages.selectedFact
            : discoverImages.unselectedFact
        }
        alt={`עובדה ${index + 1}`}
        loading="lazy"
        className="absolute ml-[100px] mt-10"
      />
    ),
    beforeChange: (_, next) => setCurrentSlide(next),
  }

  if (loadingClassic)
    return <p className="text-center">🔄 טוען אנימות קלאסיות...</p>

  if (errorClassic)
    return <p className="text-center text-red-500">❌ {errorClassic}</p>

  return (
    <section className="relative mt-20 px-2">
      <div className="flex w-full justify-between items-center mb-4 gap-2">
        <h2 className="text-h2 text-softText">נכנסים חזק לתחום</h2>
      </div>

      <div className="lg:grid lg:grid-cols-[40%_60%] lg:gap-8 mt-6 relative">
        <div className="lg:w-full grid lg:grid-rows-[auto_1fr_auto]">
          <div className="mb-2">
            <h3>דברים שלא ידעת</h3>
            <GreyBorderBottom className="w-full" />
          </div>

          <div className="relative h-[300px] lg:h-full p-4">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15"
              style={{ backgroundImage: `url(${images.redBorder})` }}
            />
            <div className="mt-10 relative max-w-[320px] md:max-w-[520px]">
              <Slider {...sliderSettings}>
                {facts.map((factObj, idx) => (
                  <div key={idx}>
                    <h2
                      className="text-xl lg:text-3xl text-white text-end"
                      dir="rtl"
                    >
                      {factObj.fact}
                    </h2>
                  </div>
                ))}
              </Slider>
              <img
                src={discoverImages.redLineDivider}
                alt="מחיצת עובדות"
                className="w-[450px]"
              />
            </div>
          </div>
        </div>

        <div className="lg:w-full mt-4 relative overflow-hidden">
          <div className="mb-2">
            <h3>קלאסיקות שחייב לראות</h3>
            <GreyBorderBottom className="w-full" />
          </div>

          <div className="w-full h-[250px] lg:h-[400px] flex justify-center items-center relative">
            {!isPlaying && selectedTrailer ? (
              <>
                <img
                  src={
                    selectedTrailer?.trailer?.images?.large_image_url ||
                    selectedTrailer?.trailer?.images?.image_url ||
                    'https://placehold.co/320x180?text=No+Trailer'
                  }
                  alt={`${selectedTrailer.title} Poster`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  title="נגן טריילר"
                  aria-label="נגן טריילר"
                  className="absolute inset-0 flex items-center justify-center bg-black/50 hover:bg-black/75 transition-all"
                  onClick={() => setIsPlaying(true)}
                >
                  <TrailerIcon className=" fill-accentLight hover:fill-primary" />
                </button>
              </>
            ) : selectedTrailer?.trailer?.embed_url ? (
              <ReactPlayer
                url={selectedTrailer.trailer.embed_url}
                playing
                controls
                width="100%"
                height="100%"
                config={{
                  youtube: {
                    playerVars: { modestbranding: 1, rel: 0, showinfo: 0 },
                  },
                }}
              />
            ) : (
              <div className="flex items-center justify-center w-full h-full bg-gray-900 text-white">
                אין טריילר זמין
              </div>
            )}
          </div>

          {selectedTrailer && (
            <div className="flex justify-between mt-2">
              <h2 className="text-white rtl-truncate">
                <span>{selectedTrailer.title_hebrew}</span> //{' '}
                <span>
                  {selectedTrailer.title_english || selectedTrailer.title}
                </span>
              </h2>

              <button
                type="button"
                onClick={() => handleToggleWatch(selectedTrailer)}
                title={
                  isInWatch(selectedTrailer.mal_id)
                    ? 'הסר מרשימה'
                    : 'הוסף לרשימה'
                }
                aria-label={
                  isInWatch(selectedTrailer.mal_id)
                    ? 'הסר מרשימה'
                    : 'הוסף לרשימה'
                }
              >
                <WatchlistIcon
                  className={
                    isInWatch(selectedTrailer.mal_id)
                      ? 'fill-primary'
                      : 'fill-[#363636]'
                  }
                />
              </button>
            </div>
          )}

          <div className="grid gap-4 mt-4 justify-center grid-cols-2 md:grid-cols-4">
            {trailers.map((trailer, idx) => (
              <div key={trailer.mal_id} className="w-full relative">
                <button
                  type="button"
                  onMouseEnter={() => setHover(trailer.mal_id)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => {
                    setSelected(trailer)
                    setIsPlaying(false)
                  }}
                  className="relative focus:outline-none w-full h-[250px]"
                  title={`נגן טריילר • ${trailer.title}`}
                  aria-label={`נגן טריילר • ${trailer.title}`}
                >
                  <img
                    src={
                      trailer.images?.webp?.large_image_url ||
                      trailer.images?.jpg?.large_image_url ||
                      'https://placehold.co/200x300?text=No+Image'
                    }
                    alt={`${trailer.title} Poster`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />

                  <div className="absolute -top-1 right-0 w-14 h-12 border-t-4 border-r-4 border-primary" />

                  <div className="absolute top-0.5 right-1 w-11 h-9 bg-primary shadow-[0_0_5px_0_#000]">
                    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl text-white font-noa">
                      {`0${idx + 1}`}
                    </span>
                  </div>

                  <div className="absolute inset-0 flex items-center justify-center">
                    <TrailerIcon
                      className={
                        hoveredTrailerId === trailer.mal_id
                          ? 'fill-primary   transition-all duration-300'
                          : 'fill-accentLight transition-all duration-300'
                      }
                    />
                  </div>
                </button>

                <div className="absolute bottom-0 w-full py-2 text-center bg-accentLight">
                  <Link
                    to={getAnimeDetailsUrl(trailer)}
                    className="text-white rtl-truncate text-lg hover:underline"
                    title={`דף אנימה: ${trailer.title}`}
                    aria-label={`מעבר לעמוד של ${trailer.title}`}
                  >
                    <span>{trailer.title_hebrew}</span> //{' '}
                    <span>{trailer.title_english || trailer.title}</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
